import { Action, LLMTaskStatus, RecordType } from "@pilatestime/shared-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ChevronDown,
	ChevronUp,
	FileText,
	Loader2,
	Plus,
	RefreshCw,
	Sparkles,
	User,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchCustomerMedicalRecordsQueryOptions,
	fetchMedicalRecordByIdQueryOptions,
	useGenerateMedicalRecordSummary,
} from "@/api/medicalRecords";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { anamnesisFormSections } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/medical-records/forms/configs/anamnesisConfig";
import { ViewMedicalRecordModal } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/medical-records/ViewMedicalRecordModal";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/medicalRecords/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, customerId } = params;

		// Preload medical records data
		context.queryClient.ensureQueryData(
			fetchCustomerMedicalRecordsQueryOptions(userId, token, orgId, customerId),
		);

		return { userId, token, orgId, customerId };
	},
	component: CustomerMedicalRecordsPage,
});

// Helper to get nested value from an object
function getNestedValue(obj: Record<string, unknown>, path: string[]): unknown {
	return path.reduce((current: unknown, key: string) => {
		return current && typeof current === "object"
			? (current as Record<string, unknown>)[key]
			: undefined;
	}, obj as unknown);
}

// Compact component to display answered form questions
function ExpandedRecordDetails({
	recordId,
	token,
	userId,
	orgId,
	customerId,
	summary,
}: {
	recordId: string;
	token: string;
	userId: string;
	orgId: string;
	customerId: string;
	summary?: string;
}) {
	const { t } = useTranslation();

	// Lazy load full record details only when expanded
	const { data: fullRecord, isLoading } = useQuery({
		...fetchMedicalRecordByIdQueryOptions(
			userId,
			token,
			orgId,
			customerId,
			recordId,
		),
		staleTime: 5 * 60 * 1000,
	});

	if (isLoading) {
		return (
			<div className="mt-4 pt-4 border-t border-base-300 flex items-center justify-center py-8">
				<Loader2 className="h-5 w-5 animate-spin text-purple-500" />
				<span className="ml-2 text-sm text-base-content/60">
					{t("common.loading")}
				</span>
			</div>
		);
	}

	if (!fullRecord) {
		return null;
	}

	// Get answered sections for anamnesis records
	const getAnsweredSections = () => {
		if (fullRecord.recordType !== RecordType.ANAMNESIS) return [];

		const sections: {
			title: string;
			items: { question: string; answers: string[] }[];
		}[] = [];

		for (const section of anamnesisFormSections) {
			const sectionItems: { question: string; answers: string[] }[] = [];

			for (const field of section.fields) {
				if (field.type === "checkboxGroup" && field.options) {
					const dataValue = getNestedValue(
						fullRecord as unknown as Record<string, unknown>,
						field.dataPath,
					);
					if (dataValue && typeof dataValue === "object") {
						const selectedAnswers = field.options
							.filter(
								(opt) =>
									(dataValue as Record<string, boolean>)[opt.key] === true,
							)
							.map((opt) => t(opt.label));

						if (selectedAnswers.length > 0) {
							sectionItems.push({
								question: t(field.title),
								answers: selectedAnswers,
							});
						}
					}
				}
			}

			if (sectionItems.length > 0) {
				sections.push({
					title: t(section.title),
					items: sectionItems,
				});
			}
		}

		return sections;
	};

	const answeredSections = getAnsweredSections();

	return (
		<div className="mt-4 pt-4 border-t border-base-300 space-y-4">
			{/* AI Summary Section */}
			{summary && (
				<div>
					<div className="flex items-center gap-2 mb-2">
						<Sparkles className="h-4 w-4 text-purple-500" />
						<span className="text-sm font-medium text-purple-600">
							{t("medicalRecords.aiSummary.title")}
						</span>
					</div>
					<p className="text-sm text-base-content/80 whitespace-pre-wrap bg-purple-50 p-3 rounded-lg border border-purple-100">
						{summary}
					</p>
				</div>
			)}

			{/* Form Answers Section */}
			{answeredSections.length > 0 && (
				<div>
					<div className="flex items-center gap-2 mb-3">
						<FileText className="h-4 w-4 text-accent" />
						<span className="text-sm font-medium text-accent">
							{t("medicalRecords.formAnswers.title")}
						</span>
					</div>
					<div className="space-y-3">
						{answeredSections.map((section) => (
							<div
								key={section.title}
								className="bg-base-200/50 rounded-lg p-3 border border-base-300"
							>
								<h4 className="text-xs font-semibold text-base-content/70 uppercase tracking-wide mb-2">
									{section.title}
								</h4>
								<div className="space-y-2">
									{section.items.map((item) => (
										<div key={item.question} className="text-sm">
											<span className="font-medium text-base-content/80">
												{item.question}:
											</span>{" "}
											<span className="text-base-content/70">
												{item.answers.join(", ")}
											</span>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function CustomerMedicalRecordsPage() {
	const { t } = useTranslation();
	const { formatDate } = useDateFormatter();
	const { orgId, customerId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const queryClient = useQueryClient();
	const [viewModalRecord, setViewModalRecord] = useState<
		(typeof medicalRecords)[0] | null
	>(null);
	const [expandedRecords, setExpandedRecords] = useState<Set<string>>(
		new Set(),
	);
	const [refreshingRecords, setRefreshingRecords] = useState<Set<string>>(
		new Set(),
	);

	// Permission check
	const can = useCan();
	const canManageMedicalRecords = can(Action.MANAGE_MEDICAL_RECORDS);

	// Get medical records data
	const { data: medicalRecordsResponse } = useQuery(
		fetchCustomerMedicalRecordsQueryOptions(userId, token, orgId, customerId),
	);
	const medicalRecords = medicalRecordsResponse?.records || [];

	// Generate summary mutation
	const generateSummaryMutation = useGenerateMedicalRecordSummary();

	const toggleExpanded = (recordId: string) => {
		setExpandedRecords((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(recordId)) {
				newSet.delete(recordId);
			} else {
				newSet.add(recordId);
			}
			return newSet;
		});
	};

	const handleGenerateSummary = (recordId: string) => {
		generateSummaryMutation.mutate({
			token,
			organizationId: orgId,
			customerId,
			recordId,
		});
	};

	// Refresh a single record to check if summary generation is complete
	const handleRefreshRecord = async (recordId: string) => {
		setRefreshingRecords((prev) => new Set(prev).add(recordId));
		try {
			// Invalidate and refetch the records list
			await queryClient.invalidateQueries({
				queryKey: ["customer_medical_records", orgId, customerId],
			});
		} finally {
			setRefreshingRecords((prev) => {
				const newSet = new Set(prev);
				newSet.delete(recordId);
				return newSet;
			});
		}
	};

	return (
		<div className="h-full flex flex-col space-y-6">
			<Card className="border-0 shadow-lg">
				<CardHeader className="bg-gradient-to-r from-accent/5 to-accent/10 border-b border-accent/10">
					<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-accent/10 rounded-lg">
								<FileText className="h-5 w-5 text-accent" />
							</div>
							<div>
								<CardTitle className="text-base sm:text-lg">
									{t("customers.medicalRecords.title")}
								</CardTitle>
								<CardDescription className="text-xs sm:text-sm">
									{t("customers.medicalRecords.description")}
								</CardDescription>
							</div>
						</div>
						{canManageMedicalRecords && (
							<Link
								to="/app/org/$orgId/customers/$customerId/medicalRecords/new"
								params={{ orgId, customerId }}
								className="btn bg-accent hover:bg-accent/90 text-accent-content w-full sm:w-auto"
							>
								<Plus className="h-4 w-4 mr-2" />
								{t("customers.medicalRecords.addRecord")}
							</Link>
						)}
					</div>
				</CardHeader>
				<CardContent className="p-6">
					{medicalRecords.length === 0 ? (
						<div className="text-center py-12">
							<div className="bg-base-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
								<FileText className="h-10 w-10 text-base-content/40" />
							</div>
							<h3 className="text-lg font-medium text-base-content mb-2">
								{t("customers.medicalRecords.noRecords")}
							</h3>
							<p className="text-base-content/60 mb-6 max-w-md mx-auto">
								{t("customers.medicalRecords.noRecordsDescription")}
							</p>
							{canManageMedicalRecords && (
								<Link
									to="/app/org/$orgId/customers/$customerId/medicalRecords/new"
									params={{ orgId, customerId }}
									className="btn btn-outline border-accent text-accent hover:bg-accent hover:text-accent-content"
								>
									<Plus className="h-4 w-4 mr-2" />
									{t("customers.medicalRecords.addRecord")}
								</Link>
							)}
						</div>
					) : (
						<div className="space-y-3">
							{medicalRecords.map((record) => {
								const isAnamnesis = record.recordType === RecordType.ANAMNESIS;
								const hasSummary = !!record.summary;
								const isExpanded = expandedRecords.has(record.id);
								// Check if summary is being generated (PROCESSING status from backend)
								const isProcessing =
									isAnamnesis &&
									record.summaryGenerationStatus === LLMTaskStatus.PROCESSING;
								const isRefreshing = refreshingRecords.has(record.id);
								// Show generate button only if no summary, not processing, and has permission
								const showGenerateButton =
									isAnamnesis &&
									!hasSummary &&
									!isProcessing &&
									canManageMedicalRecords;
								const isGenerating =
									generateSummaryMutation.isPending &&
									generateSummaryMutation.variables?.recordId === record.id;
								// Show details button for anamnesis records (with summary or answers)
								const showDetailsButton = isAnamnesis;

								return (
									<div
										key={record.id}
										className="bg-base-100 border border-base-300 rounded-xl p-4 sm:p-5 hover:shadow-md hover:border-accent/30 transition-all duration-200 group"
									>
										<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
											<div className="flex-1 min-w-0">
												<div className="flex items-start gap-3 mb-3">
													<div className="p-2 bg-accent/10 rounded-lg group-hover:bg-accent/20 transition-colors shrink-0">
														<FileText className="h-4 w-4 text-accent" />
													</div>
													<div className="flex-1 min-w-0">
														<div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
															<h3 className="font-semibold text-base-content text-base sm:text-lg truncate">
																{record.title}
															</h3>
															<span className="text-xs bg-accent/10 text-accent px-2 sm:px-3 py-1 rounded-full font-medium border border-accent/20 w-fit shrink-0">
																{t(
																	`medicalRecords.recordTypeLabels.${record.recordType}`,
																)}
															</span>
															{/* Processing Badge with Refresh Button */}
															{isProcessing && (
																<div className="flex items-center gap-1">
																	<Badge
																		variant="outline"
																		className="bg-purple-50 text-purple-600 border-purple-200 text-xs"
																	>
																		<Loader2 className="h-3 w-3 mr-1 animate-spin" />
																		{t("medicalRecords.aiSummary.processing")}
																	</Badge>
																	<Button
																		variant="ghost"
																		size="sm"
																		className="h-6 w-6 p-0 hover:bg-purple-50"
																		onClick={() =>
																			handleRefreshRecord(record.id)
																		}
																		disabled={isRefreshing}
																		title={t(
																			"medicalRecords.aiSummary.refresh",
																		)}
																	>
																		<RefreshCw
																			className={`h-3 w-3 text-purple-600 ${isRefreshing ? "animate-spin" : ""}`}
																		/>
																	</Button>
																</div>
															)}
														</div>
														{record.description && (
															<p className="text-xs sm:text-sm text-base-content/70 line-clamp-2">
																{record.description}
															</p>
														)}
													</div>
												</div>
												<div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-base-content/50">
													<div className="flex items-center gap-1">
														<User className="h-3 w-3" />
														<span className="truncate max-w-[150px] sm:max-w-none">
															{record.customerName}
														</span>
													</div>
													<div className="flex items-center gap-1">
														<span>•</span>
														<span>
															{formatDate(new Date(record.createdAt).getTime())}
														</span>
													</div>
												</div>
											</div>
											<div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
												{/* Generate AI Summary Button */}
												{showGenerateButton && (
													<Button
														variant="outline"
														size="sm"
														className="border-purple-300 text-purple-600 hover:bg-purple-50 hover:text-purple-700 w-full sm:w-auto"
														onClick={() => handleGenerateSummary(record.id)}
														disabled={isGenerating}
													>
														<Sparkles className="h-4 w-4 mr-1" />
														{isGenerating
															? t("medicalRecords.aiSummary.generating")
															: t("medicalRecords.aiSummary.generateButton")}
													</Button>
												)}
												{/* Show/Hide Details Button */}
												{showDetailsButton && (
													<Button
														variant="ghost"
														size="sm"
														className="hover:bg-purple-50 hover:text-purple-600 w-full sm:w-auto"
														onClick={() => toggleExpanded(record.id)}
													>
														{isExpanded ? (
															<>
																<ChevronUp className="h-4 w-4 mr-1" />
																{t("medicalRecords.formAnswers.hideDetails")}
															</>
														) : (
															<>
																<ChevronDown className="h-4 w-4 mr-1" />
																{t("medicalRecords.formAnswers.showDetails")}
															</>
														)}
													</Button>
												)}
												<Button
													variant="ghost"
													size="sm"
													className="hover:bg-accent/10 hover:text-accent w-full sm:w-auto"
													onClick={() => setViewModalRecord(record)}
												>
													<FileText className="h-4 w-4 mr-1" />
													{t("common.view")}
												</Button>
											</div>
										</div>
										{/* Collapsible Details Area (lazy loaded) */}
										{isExpanded && (
											<ExpandedRecordDetails
												recordId={record.id}
												token={token}
												userId={userId}
												orgId={orgId}
												customerId={customerId}
												summary={record.summary}
											/>
										)}
									</div>
								);
							})}
						</div>
					)}
				</CardContent>
			</Card>

			{/* View Medical Record Modal */}
			{viewModalRecord && (
				<ViewMedicalRecordModal
					isOpen={!!viewModalRecord}
					onClose={() => setViewModalRecord(null)}
					medicalRecord={viewModalRecord}
				/>
			)}
		</div>
	);
}
